import React, { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { FormControlLabel, TextField } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { makeStyles } from '@mui/styles';
import { fieldTypes } from '@/helpers/constants';
import { formatDate, getFieldCursorType, getNativeDateValue } from '@/helpers/utils';
import { useFieldSelection } from '@/hooks/useFieldSelection';
import useWarningField from '@/hooks/useWarningField';
import { ThemeOptions } from '@/utils/componentStyles';
import MandatoryLabel from '../MandatoryLabel/MandatoryLabel';
import HighlightMask from '../../HighlightMask/HighlightMask';

const useStyles = makeStyles(() => ({
  calendarInput: {
    width: '100%',
  },
  selectionModePointer: {
    '& *': getFieldCursorType(true),
  },
}));

type CalendarProps = {
  formControlClass?: string;
  pickerClass?: string;
  id: string;
  label: string | ReactNode;
  value: Date | null;
  handleChange?: (date: Date | null) => void;
  updateOnAccept?: boolean;
  stabilizeOpenNavigation?: boolean;
  disabled?: boolean;
  invalid?: boolean;
  isMandatory?: boolean;
  margin?: 'none' | 'dense' | 'normal';
  helperText?: string;
  fieldPath?: string[];
  isClearable?: boolean;
};

const Calendar: React.FC<CalendarProps> = (props) => {
  const { formControlClass, pickerClass, id, label, value = null, handleChange = () => { }, disabled, invalid, isMandatory = false,
    margin = 'normal', helperText, fieldPath, updateOnAccept = false, stabilizeOpenNavigation = true } = props;
  const inputLabel = isMandatory ? (<>{label} <MandatoryLabel /></>) : label;
  const [tab = '', fieldName = '', fieldSecondaryName = ''] = fieldPath || [];
  const { isFieldSelected, handleFieldSelection, isSelectionModeOn } = useFieldSelection({ tab, fieldName, fieldSecondaryName });
  const warning = useWarningField(fieldPath, fieldTypes.CALENDAR) || invalid || helperText || isFieldSelected;
  const theme = useTheme() as ThemeOptions;
  const classes = useStyles();
  const normalizedValue = useMemo(() => (value ? new Date(value) : null), [value]);
  const [localValue, setLocalValue] = useState<Date | null>(normalizedValue);
  useEffect(() => {
    if (stabilizeOpenNavigation) {
      setLocalValue(normalizedValue);
    }
  }, [normalizedValue, stabilizeOpenNavigation]);

  const handleOnChange = useCallback((date: Date | null) => {
    if (isFieldSelected) {
      handleFieldSelection();
    }

    handleChange(date);
  }, [handleChange, handleFieldSelection, isFieldSelected]);

  const handlePickerEvent = useCallback((date: Date | null, isAccepted = false) => {
    if (stabilizeOpenNavigation) {
      setLocalValue(date);
    }

    const shouldCommit = isAccepted ? updateOnAccept : !updateOnAccept;

    if (shouldCommit) {
      handleOnChange(date);
    }
  }, [handleOnChange, stabilizeOpenNavigation, updateOnAccept]);

  const pickerValue = stabilizeOpenNavigation ? localValue : normalizedValue;

  const nativeValue = useMemo(() => getNativeDateValue(pickerValue), [pickerValue]);

  const handleNativeDateChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const nextValue = event.target.value;
    const date = nextValue ? new Date(`${nextValue}T00:00:00`) : null;

    handlePickerEvent(date);
  }, [handlePickerEvent]);

  return (
    <FormControlLabel
      className={`${formControlClass} ${warning ? 'ValidationIndicatorField' : ''}`}
      label={undefined}
      control={(
        <HighlightMask
          fieldId={id}
          fieldPath={fieldPath || []}
          value={value ? formatDate(value) : ''}
          fieldType={fieldTypes.CALENDAR}
          disabled={!!disabled}
        >
          <div
            onClick={isSelectionModeOn ? handleFieldSelection : undefined}
            data-testid="date-picker-DatePicker"
          >
            <TextField
              id={id}
              data-testid={id}
              className={`${classes.calendarInput} ${pickerClass} ${isSelectionModeOn ? classes.selectionModePointer : ''}`}
              type="date"
              disabled={disabled}
              value={nativeValue}
              onChange={handleNativeDateChange}
              label={inputLabel}
              error={!!warning}
              helperText={helperText}
              variant="outlined"
              margin={margin}
              placeholder=""
              sx={{
                ...(isFieldSelected && {
                  backgroundColor: `${theme.cartTheme.alertWarningBg} !important`,
                }),
              }}
              InputLabelProps={{
                shrink: true,
              }}
              InputProps={{
                className: warning ? 'validation-warning' : '',
                autoComplete: 'off',
              }}
              inputProps={{
                style: isSelectionModeOn ? { pointerEvents: 'none' } : {},
              }}
              fullWidth
            />
          </div>
        </HighlightMask>
      )}
    />
  );
};

export default Calendar;
